import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
} from '../store/financialEntity/financialEntitySlice.ts';
import {
  selectDoraBusinessServices,
  setDoraBusinessServices,
} from '../store/businessService/businessServiceSlice.ts';
import {
  selectDoraICTServices,
  setDoraICTServices,
} from '../store/ictService/ictServiceSlice.ts';
import {
  selectDoraRisks,
  setDoraRisks,
} from '../store/risk/riskSlice.ts';
import {
  addDoraIncident,
  removeDoraIncident,
  selectDoraIncidents,
  setDoraIncidents,
  updateDoraIncident as updateDoraIncidentState,
} from '../store/incident/incidentSlice.ts';
import {
  listDoraFinancialEntities,
  listDoraBusinessServices,
  listDoraICTServices,
  listDoraRisks,
  listDoraIncidents,
  createDoraIncident,
  updateDoraIncident,
  deleteDoraIncident,
} from '../../../api.ts';
import { DoraIncident, doraIncidentToUpdate } from '../models/doraIncident.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

const SEVERITY_OPTIONS = ['minor', 'major', 'critical'];

export const IncidentsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const businessServices = useAppSelector(selectDoraBusinessServices);
  const ictServices = useAppSelector(selectDoraICTServices);
  const risks = useAppSelector(selectDoraRisks);
  const incidents = useAppSelector(selectDoraIncidents);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [severity, setSeverity] = useState<string>('minor');
  const [financialEntityId, setFinancialEntityId] = useState<number | ''>('');
  const [businessServiceId, setBusinessServiceId] = useState<number | ''>('');
  const [ictServiceId, setICTServiceId] = useState<number | ''>('');
  const [riskId, setRiskId] = useState<number | ''>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editOccurredAt, setEditOccurredAt] = useState('');
  const [editSeverity, setEditSeverity] = useState<string>('minor');
  const [editFinancialEntityId, setEditFinancialEntityId] = useState<number | ''>('');
  const [editBusinessServiceId, setEditBusinessServiceId] = useState<number | ''>('');
  const [editICTServiceId, setEditICTServiceId] = useState<number | ''>('');
  const [editRiskId, setEditRiskId] = useState<number | ''>('');

  const sortedIncidents = useMemo(
    () => [...incidents].sort((a, b) => a.title.localeCompare(b.title)),
    [incidents],
  );

  useEffect(() => {
    if (!financialEntityId && financialEntities.length > 0) {
      setFinancialEntityId(financialEntities[0].id ?? '');
    }
    if (!editFinancialEntityId && financialEntities.length > 0 && editingId) {
      setEditFinancialEntityId(financialEntities[0].id ?? '');
    }
  }, [financialEntities, financialEntityId, editFinancialEntityId, editingId]);

  useEffect(() => {
    if (!occurredAt) {
      setOccurredAt(new Date().toISOString().slice(0, 16));
    }
  }, [occurredAt]);

  const createDisabled = !title.trim() || !financialEntityId;

  const severityColor = (value?: string | null) => {
    switch (value) {
      case 'critical':
        return 'error';
      case 'major':
        return 'warning';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [fes, bs, icts, riskList, incidentList] = await Promise.all([
          listDoraFinancialEntities(),
          listDoraBusinessServices(),
          listDoraICTServices(),
          listDoraRisks(),
          listDoraIncidents(),
        ]);
        dispatch(setDoraFinancialEntities(fes));
        dispatch(setDoraBusinessServices(bs));
        dispatch(setDoraICTServices(icts));
        dispatch(setDoraRisks(riskList));
        dispatch(setDoraIncidents(incidentList));
      } catch (err: any) {
        console.error('Failed to load incidents', err);
        setError(err?.response?.data?.detail || 'Failed to load incidents');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!title.trim() || !financialEntityId) {
      setError('Title and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraIncident({
        title: title.trim(),
        description: description || null,
        occurred_at: occurredAt || new Date().toISOString(),
        severity,
        financial_entity_id: Number(financialEntityId),
        business_service_id: businessServiceId === '' ? null : Number(businessServiceId),
        ict_service_id: ictServiceId === '' ? null : Number(ictServiceId),
        risk_id: riskId === '' ? null : Number(riskId),
      });
      dispatch(addDoraIncident(created));
      setTitle('');
      setDescription('');
      setOccurredAt('');
      setSeverity('minor');
      setFinancialEntityId('');
      setBusinessServiceId('');
      setICTServiceId('');
      setRiskId('');
      setSuccess('Incident created');
    } catch (err: any) {
      console.error('Failed to create incident', err);
      setError(err?.response?.data?.detail || 'Failed to create incident');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (incident: DoraIncident) => {
    setEditingId(incident.id ?? null);
    setEditTitle(incident.title);
    setEditDescription(incident.description || '');
    setEditOccurredAt(incident.occurred_at ? incident.occurred_at.slice(0, 16) : '');
    setEditSeverity(incident.severity || 'minor');
    setEditFinancialEntityId(incident.financial_entity_id);
    setEditBusinessServiceId(incident.business_service_id ?? '');
    setEditICTServiceId(incident.ict_service_id ?? '');
    setEditRiskId(incident.risk_id ?? '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
    setEditOccurredAt('');
    setEditSeverity('minor');
    setEditFinancialEntityId('');
    setEditBusinessServiceId('');
    setEditICTServiceId('');
    setEditRiskId('');
  };

  const handleUpdate = async (incident: DoraIncident) => {
    if (!incident.id) return;
    if (!editTitle.trim() || !editFinancialEntityId) {
      setError('Title and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraIncidentToUpdate({
        ...incident,
        title: editTitle.trim(),
        description: editDescription || null,
        occurred_at: editOccurredAt ? new Date(editOccurredAt).toISOString() : incident.occurred_at,
        severity: editSeverity,
        financial_entity_id: Number(editFinancialEntityId),
        business_service_id: editBusinessServiceId === '' ? null : Number(editBusinessServiceId),
        ict_service_id: editICTServiceId === '' ? null : Number(editICTServiceId),
        risk_id: editRiskId === '' ? null : Number(editRiskId),
      });
      const updated = await updateDoraIncident(incident.id, payload);
      dispatch(updateDoraIncidentState(updated));
      cancelEdit();
      setSuccess('Incident updated');
    } catch (err: any) {
      console.error('Failed to update incident', err);
      setError(err?.response?.data?.detail || 'Failed to update incident');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this incident?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraIncident(id);
      dispatch(removeDoraIncident(id));
      setSuccess('Incident deleted');
    } catch (err: any) {
      console.error('Failed to delete incident', err);
      setError(err?.response?.data?.detail || 'Failed to delete incident');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Incident
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="incident-severity-label">Severity</InputLabel>
              <Select
                labelId="incident-severity-label"
                value={severity}
                label="Severity"
                onChange={(e: SelectChangeEvent<string>) => setSeverity(e.target.value)}
              >
                {SEVERITY_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Occurred At"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel id="incident-fe-label">Financial Entity</InputLabel>
              <Select
                labelId="incident-fe-label"
                value={financialEntityId === '' ? '' : String(financialEntityId)}
                label="Financial Entity"
                onChange={(e: SelectChangeEvent<string>) => setFinancialEntityId(Number(e.target.value))}
              >
                {financialEntities.map(fe => (
                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="incident-bs-label" shrink>Business Service</InputLabel>
              <Select
                labelId="incident-bs-label"
                value={businessServiceId === '' ? '' : String(businessServiceId)}
                label="Business Service"
                onChange={(e: SelectChangeEvent<string>) => setBusinessServiceId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {businessServices.map(bs => (
                  <MenuItem key={bs.id} value={bs.id}>{bs.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="incident-ict-label" shrink>ICT Service</InputLabel>
              <Select
                labelId="incident-ict-label"
                value={ictServiceId === '' ? '' : String(ictServiceId)}
                label="ICT Service"
                onChange={(e: SelectChangeEvent<string>) => setICTServiceId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {ictServices.map(ict => (
                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="incident-risk-label" shrink>Linked Risk</InputLabel>
              <Select
                labelId="incident-risk-label"
                value={riskId === '' ? '' : String(riskId)}
                label="Linked Risk"
                onChange={(e: SelectChangeEvent<string>) => setRiskId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {risks.map(risk => (
                  <MenuItem key={risk.id} value={risk.id}>{risk.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {financialEntities.length === 0 && (
        <Alert severity="warning">
          No Financial Entities found. Please create financial entities in the <strong>Financial Entities</strong> panel (B_01.02) before creating incidents.
        </Alert>
      )}

      {(businessServices.length === 0 || ictServices.length === 0 || risks.length === 0) && (
        <Alert severity="info">
          Missing optional data for linking incidents:{' '}
          {businessServices.length === 0 && <><strong>Business Services</strong> (Business Services panel), </>}
          {ictServices.length === 0 && <><strong>ICT Services</strong> (ICT Services panel), </>}
          {risks.length === 0 && <><strong>Risks</strong> (Risks panel)</>}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Incidents</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedIncidents.length === 0 ? (
            <Alert severity="info">No incidents yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Occurred At</TableCell>
                    <TableCell>Financial Entity</TableCell>
                    <TableCell>Business Service</TableCell>
                    <TableCell>ICT Service</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedIncidents.map(incident => {
                    const isEditing = editingId === incident.id;
                    const feName = financialEntities.find(fe => fe.id === incident.financial_entity_id)?.name || '—';
                    const bsName = incident.business_service_id
                      ? (businessServices.find(bs => bs.id === incident.business_service_id)?.name || '—')
                      : 'None';
                    const ictName = incident.ict_service_id
                      ? (ictServices.find(ict => ict.id === incident.ict_service_id)?.name || '—')
                      : 'None';
                    const riskTitle = incident.risk_id
                      ? (risks.find(r => r.id === incident.risk_id)?.title || '—')
                      : 'None';
                    return (
                      <TableRow key={incident.id ?? incident.title}>
                        <TableCell width="18%">
                          {isEditing ? (
                            <TextField value={editTitle} onChange={(e) => setEditTitle(e.target.value)} size="small" fullWidth />
                          ) : incident.title}
                        </TableCell>
                        <TableCell width="12%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editSeverity}
                                onChange={(e: SelectChangeEvent<string>) => setEditSeverity(e.target.value)}
                              >
                                {SEVERITY_OPTIONS.map(option => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip label={incident.severity || '—'} size="small" color={severityColor(incident.severity) as any} />
                          )}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <TextField
                              type="datetime-local"
                              value={editOccurredAt}
                              onChange={(e) => setEditOccurredAt(e.target.value)}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : new Date(incident.occurred_at).toLocaleString()}
                        </TableCell>
                        <TableCell width="14%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editFinancialEntityId === '' ? '' : String(editFinancialEntityId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditFinancialEntityId(Number(e.target.value))}
                              >
                                {financialEntities.map(fe => (
                                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : feName}
                        </TableCell>
                        <TableCell width="14%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editBusinessServiceId === '' ? '' : String(editBusinessServiceId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditBusinessServiceId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {businessServices.map(bs => (
                                  <MenuItem key={bs.id} value={bs.id}>{bs.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : bsName}
                        </TableCell>
                        <TableCell width="14%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editICTServiceId === '' ? '' : String(editICTServiceId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditICTServiceId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {ictServices.map(ict => (
                                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : ictName}
                        </TableCell>
                        <TableCell width="14%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editRiskId === '' ? '' : String(editRiskId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditRiskId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {risks.map(risk => (
                                  <MenuItem key={risk.id} value={risk.id}>{risk.title}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : riskTitle}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(incident)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(incident)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(incident.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default IncidentsPanel;
